import React, { useMemo, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiBookOpen,
  FiClock,
  FiActivity,
  FiEdit2,
  FiFilter,
  FiList,
  FiTrendingUp
} = FiIcons

const ActivityLogs = () => {
  const [readingLog, setReadingLog] = useState({
    title: '',
    author: '',
    pages: '',
    minutes: '',
    date: '',
    notes: ''
  })
  const [peLog, setPeLog] = useState({
    activity: '',
    duration: '',
    date: '',
    notes: ''
  })
  const [reflection, setReflection] = useState({
    cadence: 'Daily',
    prompt: 'What was the most meaningful part of today?',
    response: '',
    parentNotes: ''
  })
  const [filter, setFilter] = useState('all')
  const [history, setHistory] = useState([
    {
      type: 'reading',
      title: 'Science: Oceans',
      author: 'J. Rivera',
      pages: 24,
      minutes: 35,
      date: '2024-11-04',
      notes: 'Summarized each section and highlighted new vocabulary.'
    },
    {
      type: 'pe',
      activity: 'Soccer Drills',
      duration: 45,
      date: '2024-11-05',
      notes: 'Practiced passes and agility ladder.'
    },
    {
      type: 'reflection',
      cadence: 'Weekly',
      prompt: 'How did you show perseverance?',
      response: 'Finished a tough math set and asked for help instead of giving up.',
      parentNotes: 'Recognized effort and set up a study buddy.',
      date: '2024-11-03'
    }
  ])

  const addReadingEntry = (event) => {
    event.preventDefault()
    setHistory((prev) => [
      {
        type: 'reading',
        ...readingLog,
        pages: Number(readingLog.pages) || 0,
        minutes: Number(readingLog.minutes) || 0,
        date: readingLog.date || new Date().toISOString().slice(0, 10)
      },
      ...prev
    ])
    setReadingLog({ title: '', author: '', pages: '', minutes: '', date: '', notes: '' })
  }

  const addPeEntry = (event) => {
    event.preventDefault()
    setHistory((prev) => [
      {
        type: 'pe',
        ...peLog,
        duration: Number(peLog.duration) || 0,
        date: peLog.date || new Date().toISOString().slice(0, 10)
      },
      ...prev
    ])
    setPeLog({ activity: '', duration: '', date: '', notes: '' })
  }

  const addReflectionEntry = (event) => {
    event.preventDefault()
    setHistory((prev) => [
      {
        type: 'reflection',
        ...reflection,
        date: new Date().toISOString().slice(0, 10)
      },
      ...prev
    ])
    setReflection((prevState) => ({ ...prevState, response: '', parentNotes: '' }))
  }

  const filteredHistory = useMemo(() => {
    if (filter === 'all') return history
    return history.filter((entry) => entry.type === filter)
  }, [filter, history])

  const totals = useMemo(() => {
    return history.reduce(
      (acc, entry) => {
        if (entry.type === 'reading') {
          acc.pages += entry.pages || 0
          acc.minutes += entry.minutes || 0
        }
        if (entry.type === 'pe') {
          acc.activityMinutes += entry.duration || 0
        }
        return acc
      },
      { pages: 0, minutes: 0, activityMinutes: 0 }
    )
  }, [history])

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-3">Activity Logs & Reflection</h1>
        <p className="text-blue-50 max-w-3xl">
          Capture daily reading, movement, and reflective growth with structured prompts, rich notes,
          and at-a-glance totals for students, parents, and teachers.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Reading Log */}
        <div className="bg-white rounded-xl shadow-lg p-6 xl:col-span-2">
          <h2 className="text-xl font-bold text-gray-800 flex items-center mb-4">
            <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2 text-[#00AAFF]" />
            Reading Log
          </h2>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={addReadingEntry}>
            <input
              className="border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Book title"
              value={readingLog.title}
              onChange={(e) => setReadingLog({ ...readingLog, title: e.target.value })}
              required
            />
            <input
              className="border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Author"
              value={readingLog.author}
              onChange={(e) => setReadingLog({ ...readingLog, author: e.target.value })}
              required
            />
            <input
              className="border border-gray-200 rounded-lg px-3 py-2"
              type="number"
              min="0"
              placeholder="Pages read"
              value={readingLog.pages}
              onChange={(e) => setReadingLog({ ...readingLog, pages: e.target.value })}
              required
            />
            <input
              className="border border-gray-200 rounded-lg px-3 py-2"
              type="number"
              min="0"
              placeholder="Minutes"
              value={readingLog.minutes}
              onChange={(e) => setReadingLog({ ...readingLog, minutes: e.target.value })}
              required
            />
            <input
              className="border border-gray-200 rounded-lg px-3 py-2"
              type="date"
              value={readingLog.date}
              onChange={(e) => setReadingLog({ ...readingLog, date: e.target.value })}
            />
            <textarea
              className="border border-gray-200 rounded-lg px-3 py-2 md:col-span-2"
              rows="2"
              placeholder="Notes or narration highlights"
              value={readingLog.notes}
              onChange={(e) => setReadingLog({ ...readingLog, notes: e.target.value })}
            />
            <button
              type="submit"
              className="md:col-span-2 bg-[#00AAFF] text-white py-2 rounded-lg hover:bg-[#0095E0] transition-colors"
            >
              Add Reading Entry
            </button>
          </form>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 mr-2 text-[#00AAFF]" />
            Weekly Totals
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center text-gray-700">
                <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2 text-[#1B4C87]" />
                Pages read
              </div>
              <span className="font-bold text-gray-900">{totals.pages} pages</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center text-gray-700">
                <SafeIcon icon={FiClock} className="w-5 h-5 mr-2 text-[#1B4C87]" />
                Reading minutes
              </div>
              <span className="font-bold text-gray-900">{totals.minutes} min</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center text-gray-700">
                <SafeIcon icon={FiActivity} className="w-5 h-5 mr-2 text-[#1B4C87]" />
                P.E. minutes
              </div>
              <span className="font-bold text-gray-900">{totals.activityMinutes} min</span>
            </div>
          </div>

          <div className="bg-[#00AAFF]/10 border border-[#00AAFF]/20 rounded-lg p-3 text-sm text-[#0C3B72]">
            Keep literacy, movement, and reflection in one place with quick filters and narrative notes.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* P.E. Log */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center mb-4">
            <SafeIcon icon={FiActivity} className="w-5 h-5 mr-2 text-[#00AAFF]" />
            P.E. Log
          </h2>
          <form className="space-y-3" onSubmit={addPeEntry}>
            <input
              className="border border-gray-200 rounded-lg px-3 py-2 w-full"
              placeholder="Activity type (e.g., running, dance, hiking)"
              value={peLog.activity}
              onChange={(e) => setPeLog({ ...peLog, activity: e.target.value })}
              required
            />
            <input
              className="border border-gray-200 rounded-lg px-3 py-2 w-full"
              type="number"
              min="0"
              placeholder="Duration (minutes)"
              value={peLog.duration}
              onChange={(e) => setPeLog({ ...peLog, duration: e.target.value })}
              required
            />
            <input
              className="border border-gray-200 rounded-lg px-3 py-2 w-full"
              type="date"
              value={peLog.date}
              onChange={(e) => setPeLog({ ...peLog, date: e.target.value })}
            />
            <textarea
              className="border border-gray-200 rounded-lg px-3 py-2 w-full"
              rows="3"
              placeholder="Notes (skills practiced, heart rate, effort)"
              value={peLog.notes}
              onChange={(e) => setPeLog({ ...peLog, notes: e.target.value })}
            />
            <button
              type="submit"
              className="w-full bg-[#00AAFF] text-white py-2 rounded-lg hover:bg-[#0095E0] transition-colors"
            >
              Add P.E. Entry
            </button>
          </form>
        </div>

        {/* Reflection Journal */}
        <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2">
          <h2 className="text-xl font-bold text-gray-800 flex items-center mb-4">
            <SafeIcon icon={FiEdit2} className="w-5 h-5 mr-2 text-[#00AAFF]" />
            Reflection Journal
          </h2>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={addReflectionEntry}>
            <select
              className="border border-gray-200 rounded-lg px-3 py-2"
              value={reflection.cadence}
              onChange={(e) => setReflection({ ...reflection, cadence: e.target.value })}
            >
              <option>Daily</option>
              <option>Weekly</option>
            </select>
            <select
              className="border border-gray-200 rounded-lg px-3 py-2"
              value={reflection.prompt}
              onChange={(e) => setReflection({ ...reflection, prompt: e.target.value })}
            >
              <option>What was the most meaningful part of today?</option>
              <option>How did you show perseverance?</option>
              <option>Where did you see God&apos;s goodness today?</option>
              <option>What will you try differently next week?</option>
            </select>
            <textarea
              className="border border-gray-200 rounded-lg px-3 py-2 md:col-span-2"
              rows="3"
              placeholder="Student response"
              value={reflection.response}
              onChange={(e) => setReflection({ ...reflection, response: e.target.value })}
              required
            />
            <textarea
              className="border border-gray-200 rounded-lg px-3 py-2 md:col-span-2"
              rows="2"
              placeholder="Parent comments or encouragement"
              value={reflection.parentNotes}
              onChange={(e) => setReflection({ ...reflection, parentNotes: e.target.value })}
            />
            <button
              type="submit"
              className="md:col-span-2 bg-[#1B4C87] text-white py-2 rounded-lg hover:bg-[#163d6d] transition-colors"
            >
              Save Reflection
            </button>
          </form>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiList} className="w-5 h-5 mr-2 text-[#00AAFF]" />
            Log History & Filters
          </h3>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-500" />
            <select
              className="border border-gray-200 rounded-lg px-3 py-2"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All entries</option>
              <option value="reading">Reading only</option>
              <option value="pe">P.E. only</option>
              <option value="reflection">Reflections only</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredHistory.map((entry, index) => (
            <div
              key={`${entry.type}-${index}`}
              className="border border-gray-200 rounded-lg p-4 space-y-2 bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">
                  {entry.type === 'reading' && 'Reading Log'}
                  {entry.type === 'pe' && 'P.E. Log'}
                  {entry.type === 'reflection' && 'Reflection'}
                </span>
                <span className="text-xs text-gray-500">{entry.date}</span>
              </div>
              {entry.type === 'reading' && (
                <>
                  <p className="font-semibold text-gray-800">{entry.title}</p>
                  <p className="text-sm text-gray-600">by {entry.author}</p>
                  <p className="text-sm text-gray-700">
                    {entry.pages} pages • {entry.minutes} min
                  </p>
                  {entry.notes && <p className="text-sm text-gray-600">{entry.notes}</p>}
                </>
              )}
              {entry.type === 'pe' && (
                <>
                  <p className="font-semibold text-gray-800">{entry.activity}</p>
                  <p className="text-sm text-gray-700">{entry.duration} minutes</p>
                  {entry.notes && <p className="text-sm text-gray-600">{entry.notes}</p>}
                </>
              )}
              {entry.type === 'reflection' && (
                <>
                  <p className="font-semibold text-gray-800">{entry.prompt}</p>
                  <p className="text-sm text-gray-700">Cadence: {entry.cadence}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{entry.response}</p>
                  {entry.parentNotes && (
                    <p className="text-sm text-gray-600">Parent: {entry.parentNotes}</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ActivityLogs
